export type FocusTimerMode = 'pomodoro' | 'deepFocus';
export type FocusSessionType = 'POMODORO' | 'DEEP_FOCUS';
export type FocusTimerPhase = 'focus' | 'break';

export type FocusTimerPreset = {
  mode: FocusTimerMode;
  /** Time-log source the API stores this mode's focus sessions as. */
  focusType: FocusSessionType;
  label: string;
  icon: string;
  ratioLabel: string;
  focusMinutes: number;
  breakMinutes: number;
  /** Chip accent so the control stays visible on dark boards. */
  accent: string;
  chipFill: string;
};

export const FOCUS_TIMER_PRESETS: Record<FocusTimerMode, FocusTimerPreset> = {
  pomodoro: {
    mode: 'pomodoro',
    focusType: 'POMODORO',
    label: 'Pomodoro',
    icon: '🍅',
    ratioLabel: '25/5',
    focusMinutes: 25,
    breakMinutes: 5,
    accent: '#E24B4A',
    chipFill: 'rgba(226, 75, 74, 0.22)',
  },
  deepFocus: {
    mode: 'deepFocus',
    focusType: 'DEEP_FOCUS',
    label: 'Deep Focus',
    icon: '🧠',
    ratioLabel: '90/15',
    focusMinutes: 90,
    breakMinutes: 15,
    accent: '#8B7CFF',
    chipFill: 'rgba(139, 124, 255, 0.22)',
  },
};

export type ActiveFocusTimer = {
  taskId: number;
  mode: FocusTimerMode;
  phase: FocusTimerPhase;
  /** Epoch ms when the current phase ends. */
  endsAt: number;
  /** Epoch ms when the focus phase began. */
  focusStartedAt: number;
  /** Epoch ms when the focus phase finished; set once the break begins. */
  focusEndedAt: number | null;
};

export function focusSecondsForPhase(
  mode: FocusTimerMode,
  phase: FocusTimerPhase,
): number {
  const preset = FOCUS_TIMER_PRESETS[mode];
  return (phase === 'focus' ? preset.focusMinutes : preset.breakMinutes) * 60;
}

export function startFocusTimer(
  taskId: number,
  mode: FocusTimerMode,
  nowMs = Date.now(),
): ActiveFocusTimer {
  return {
    taskId,
    mode,
    phase: 'focus',
    endsAt: nowMs + focusSecondsForPhase(mode, 'focus') * 1000,
    focusStartedAt: nowMs,
    focusEndedAt: null,
  };
}

export function remainingSeconds(endsAt: number, nowMs = Date.now()): number {
  return Math.max(0, Math.ceil((endsAt - nowMs) / 1000));
}

export function formatFocusCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Advance the timer when a phase reaches zero.
 * Focus → break; break → null (session finished).
 */
export function advanceFocusTimer(
  timer: ActiveFocusTimer,
  nowMs = Date.now(),
): ActiveFocusTimer | null {
  if (remainingSeconds(timer.endsAt, nowMs) > 0) return timer;
  if (timer.phase === 'focus') {
    return {
      taskId: timer.taskId,
      mode: timer.mode,
      phase: 'break',
      endsAt: nowMs + focusSecondsForPhase(timer.mode, 'break') * 1000,
      focusStartedAt: timer.focusStartedAt,
      // The scheduled end, not the (slightly later) tick that noticed it.
      focusEndedAt: timer.endsAt,
    };
  }
  return null;
}

export function focusPhaseLabel(phase: FocusTimerPhase): string {
  return phase === 'focus' ? 'Focus' : 'Break';
}

/** The time log stores whole minutes, so shorter sessions are not saved. */
export const MIN_LOGGED_FOCUS_SECONDS = 60;

export type FocusSession = {
  taskId: number;
  mode: FocusTimerMode;
  focusType: FocusSessionType;
  startedAt: Date;
  endedAt: Date;
  durationSeconds: number;
};

/**
 * The focus time actually spent when a session ends (Stop / ✕, finishing the
 * break, or switching to another timer). Break time is never counted: a
 * session stopped during its break ends when its focus phase ended.
 */
export function focusSessionFromTimer(
  timer: ActiveFocusTimer,
  nowMs = Date.now(),
): FocusSession {
  const endMs =
    timer.phase === 'break' && timer.focusEndedAt != null
      ? timer.focusEndedAt
      : Math.min(nowMs, timer.endsAt);
  const durationSeconds = Math.max(0, Math.floor((endMs - timer.focusStartedAt) / 1000));
  return {
    taskId: timer.taskId,
    mode: timer.mode,
    focusType: FOCUS_TIMER_PRESETS[timer.mode].focusType,
    startedAt: new Date(timer.focusStartedAt),
    endedAt: new Date(endMs),
    durationSeconds,
  };
}
